import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AlertTriangle, LifeBuoy, Plus } from 'lucide-react';
import { PageHeader, TableSkeleton } from '@/components/shared/page';
import { DataTable, type Column } from '@/components/shared/data-table';
import { Can } from '@/components/shared/can';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Avatar, Select } from '@/components/ui/misc';
import { usePagination } from '@/hooks/use-pagination';
import { useDebounce } from '@/hooks/use-debounce';
import { formatDate } from '@/lib/format';
import { cn } from '@/lib/utils';
import { useTicketBoard, useTickets } from './api';
import { TicketDialog } from './ticket-dialog';
import {
  SLA_COLOUR_CLASS,
  SLA_DOT_CLASS,
  TICKET_PRIORITY_LABELS,
  TICKET_PRIORITY_VARIANT,
  TICKET_STATUS_LABELS,
  TICKET_STATUS_VARIANT,
  TICKET_TYPE_LABELS,
  timeUntil,
} from './labels';
import type { Ticket } from './types';

export function TicketsPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const view = params.get('view') ?? 'queue';
  const [creating, setCreating] = useState(false);

  return (
    <div>
      <PageHeader
        title="Service desk"
        description="SLA clocks count working hours only, and stop while a ticket is waiting on the client."
        actions={
          <Can permission="service.ticket.create">
            <Button onClick={() => setCreating(true)}>
              <Plus className="h-4 w-4" />
              New ticket
            </Button>
          </Can>
        }
      />

      <div className="mb-4 flex gap-1 rounded-lg border p-0.5 w-fit">
        {(['queue', 'board'] as const).map((value) => (
          <button
            key={value}
            onClick={() => setParams({ view: value })}
            className={cn(
              'rounded-md px-3 py-1.5 text-sm capitalize transition-colors',
              view === value ? 'bg-secondary font-medium' : 'text-muted-foreground',
            )}
          >
            {value}
          </button>
        ))}
      </div>

      {view === 'queue' ? (
        <Queue onOpen={(id) => navigate(`/service/tickets/${id}`)} />
      ) : (
        <Board onOpen={(id) => navigate(`/service/tickets/${id}`)} />
      )}

      {creating && (
        <TicketDialog
          onClose={() => setCreating(false)}
          onSaved={(ticket) => navigate(`/service/tickets/${ticket.id}`)}
        />
      )}
    </div>
  );
}

function Queue({ onOpen }: { onOpen: (id: string) => void }) {
  const pagination = usePagination();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [priority, setPriority] = useState('');
  const [mine, setMine] = useState(false);
  const [breached, setBreached] = useState(false);
  const debounced = useDebounce(search, 300);

  const { data, isLoading, error, refetch } = useTickets({
    page: pagination.page,
    limit: pagination.limit,
    q: debounced || undefined,
    status: status || undefined,
    priority: priority || undefined,
    mine: mine || undefined,
    breachedOnly: breached || undefined,
    openOnly: !status && !breached ? true : undefined,
  });

  const columns: Column<Ticket>[] = [
    {
      key: 'subject',
      header: 'Ticket',
      cell: (row) => (
        <div className="flex items-start gap-2.5">
          {row.sla && (
            <span
              className={cn('mt-1.5 h-2 w-2 shrink-0 rounded-full', SLA_DOT_CLASS[row.sla.colour])}
              title={row.sla.policyName ?? 'SLA'}
            />
          )}
          <div className="min-w-0">
            <div className="truncate font-medium">{row.subject}</div>
            <div className="truncate text-xs text-muted-foreground">
              <span className="font-mono">{row.number}</span> · {row.company.name}
              {row.contract && ` · ${row.contract.number}`}
            </div>
          </div>
        </div>
      ),
    },
    {
      key: 'type',
      header: 'Type',
      className: 'text-sm text-muted-foreground',
      cell: (row) => TICKET_TYPE_LABELS[row.ticketType],
    },
    {
      key: 'priority',
      header: 'Priority',
      cell: (row) => (
        <Badge variant={TICKET_PRIORITY_VARIANT[row.priority]}>
          {TICKET_PRIORITY_LABELS[row.priority]}
        </Badge>
      ),
    },
    {
      key: 'sla',
      header: 'Resolution due',
      cell: (row) =>
        !row.sla ? (
          <span className="text-xs text-muted-foreground" title="No SLA policy matched">
            no policy
          </span>
        ) : (
          <div className="text-sm">
            <span className={cn('font-medium', SLA_COLOUR_CLASS[row.sla.colour])}>
              {row.sla.paused ? 'paused' : timeUntil(row.sla.resolution.dueAt)}
            </span>
            <div className="text-[11px] text-muted-foreground">
              {formatDate(row.sla.resolution.dueAt)}
            </div>
          </div>
        ),
    },
    {
      key: 'assignee',
      header: 'Assignee',
      cell: (row) =>
        row.assignee ? (
          <span className="flex items-center gap-2 text-sm">
            <Avatar name={row.assignee.name} className="h-6 w-6 text-[10px]" />
            <span className="truncate">{row.assignee.name}</span>
          </span>
        ) : (
          <span className="text-sm text-muted-foreground">Unassigned</span>
        ),
    },
    {
      key: 'status',
      header: 'Status',
      cell: (row) => (
        <Badge variant={TICKET_STATUS_VARIANT[row.status]}>
          {TICKET_STATUS_LABELS[row.status]}
        </Badge>
      ),
    },
  ];

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Input
          placeholder="Search subject, number or client…"
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            pagination.reset();
          }}
          className="h-9 max-w-xs"
        />
        <Select
          value={status}
          onChange={(event) => {
            setStatus(event.target.value);
            pagination.reset();
          }}
          className="h-9 w-auto min-w-[10rem]"
        >
          <option value="">Open tickets</option>
          {Object.entries(TICKET_STATUS_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </Select>
        <Select
          value={priority}
          onChange={(event) => {
            setPriority(event.target.value);
            pagination.reset();
          }}
          className="h-9 w-auto"
        >
          <option value="">Any priority</option>
          {Object.entries(TICKET_PRIORITY_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </Select>
        <Button
          size="sm"
          variant={mine ? 'secondary' : 'outline'}
          onClick={() => {
            setMine((value) => !value);
            pagination.reset();
          }}
        >
          Mine
        </Button>
        <Button
          size="sm"
          variant={breached ? 'destructive' : 'outline'}
          onClick={() => {
            setBreached((value) => !value);
            pagination.reset();
          }}
        >
          <AlertTriangle className="h-3.5 w-3.5" />
          Breached
        </Button>
      </div>

      <DataTable
        columns={columns}
        rows={data?.data ?? []}
        rowKey={(row) => row.id}
        isLoading={isLoading}
        error={error ? 'Could not load tickets.' : null}
        onRetry={() => void refetch()}
        meta={data?.meta}
        onPageChange={pagination.setPage}
        onRowClick={(row) => onOpen(row.id)}
        emptyTitle="Nothing in the queue"
        emptyDescription="Either everything is answered, or no tickets match these filters."
      />
    </div>
  );
}

function Board({ onOpen }: { onOpen: (id: string) => void }) {
  const { data, isLoading } = useTicketBoard();

  if (isLoading) return <TableSkeleton columns={5} />;
  if (!data) return null;

  return (
    <div>
      <div className="mb-4 flex gap-4 text-sm">
        <span className="text-muted-foreground">
          <span className="font-semibold text-amber-600">{data.atRisk}</span> at risk
        </span>
        <span className="text-muted-foreground">
          <span className="font-semibold text-destructive">{data.breached}</span> breached
        </span>
      </div>

      <div className="scrollbar-thin flex gap-3 overflow-x-auto pb-4">
        {data.columns.map((column) => (
          <div key={column.status} className="flex w-72 shrink-0 flex-col rounded-lg border bg-muted/30">
            <div className="flex items-baseline justify-between border-b px-3 py-2.5">
              <span className="text-sm font-medium">
                {TICKET_STATUS_LABELS[column.status]}
                <span className="ms-2 text-xs text-muted-foreground">{column.count}</span>
              </span>
              {column.breached > 0 && (
                <span className="text-xs font-medium text-destructive">
                  {column.breached} breached
                </span>
              )}
            </div>

            <div className="scrollbar-thin flex-1 space-y-2 overflow-y-auto p-2">
              {column.items.map((ticket) => (
                <button
                  key={ticket.id}
                  onClick={() => onOpen(ticket.id)}
                  className="w-full rounded-lg border bg-background p-3 text-start shadow-sm transition-shadow hover:shadow-md"
                >
                  <div className="flex items-center gap-1.5">
                    {ticket.sla && (
                      <span
                        className={cn('h-2 w-2 rounded-full', SLA_DOT_CLASS[ticket.sla.colour])}
                      />
                    )}
                    <span className="font-mono text-[11px] text-muted-foreground">
                      {ticket.number}
                    </span>
                    <Badge
                      variant={TICKET_PRIORITY_VARIANT[ticket.priority]}
                      className="ms-auto text-[10px]"
                    >
                      {TICKET_PRIORITY_LABELS[ticket.priority]}
                    </Badge>
                  </div>
                  <p className="mt-1.5 line-clamp-2 text-sm">{ticket.subject}</p>
                  <p className="mt-1 truncate text-xs text-muted-foreground">
                    {ticket.company.name}
                  </p>
                  <div className="mt-2 flex items-center gap-2">
                    {ticket.sla && (
                      <span className={cn('text-[11px]', SLA_COLOUR_CLASS[ticket.sla.colour])}>
                        {ticket.sla.paused ? 'clock paused' : timeUntil(ticket.sla.resolution.dueAt)}
                      </span>
                    )}
                    {ticket.assignee && (
                      <Avatar
                        name={ticket.assignee.name}
                        className="ms-auto h-6 w-6 text-[10px]"
                      />
                    )}
                  </div>
                </button>
              ))}
              {column.items.length === 0 && (
                <p className="py-6 text-center text-xs text-muted-foreground">Nothing here</p>
              )}
            </div>
          </div>
        ))}
      </div>

      {data.columns.every((column) => column.count === 0) && (
        <Card>
          <CardContent className="py-14 text-center">
            <LifeBuoy className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">The queue is empty.</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
